/* eslint max-classes-per-file: 0 */
import { TriggerTypes } from '@/models/Build';
import { Runnable } from '@/models/Runnable';
import { formatDateTime } from '@/utils/commonFormats';
import { pluralizeWordWithCount } from '@/utils/pluralizedWordWithCount';

export abstract class BaseRunnableDataMapper<TRunnable extends Runnable<unknown, never>> {
  getTriggeredBy(runnable: TRunnable): string | undefined {
    if (!runnable.triggeredBy) {
      return undefined;
    }

    if (runnable.triggeredBy === 'schedule') {
      return 'Schedule';
    }
    if (runnable.triggeredBy === 'webhook') {
      return 'Webhook';
    }
    if (runnable.triggeredBy === 'curl') {
      return 'cURL';
    }

    const manualPrefix = 'manual-';

    if (runnable.triggeredBy.startsWith(manualPrefix)) {
      const username = runnable.triggeredBy.replace(manualPrefix, '');
      return `Manual - @${username}`;
    }

    const webhookPrefix = 'webhook-';

    if (runnable.triggeredBy.startsWith(webhookPrefix)) {
      const providerAndUserName = runnable.triggeredBy.replace(webhookPrefix, '');
      return `Webhook - @${providerAndUserName}`;
    }

    return runnable.triggeredBy;
  }

  getTriggeredAt(runnable: TRunnable): string {
    return runnable.triggeredAt
      ? formatDateTime(runnable.triggeredAt, { hideYear: true, includeTimeZone: true })
      : 'Not triggered yet';
  }

  getStartedAt(runnable: TRunnable): string {
    const startedAt = this.getDurationStartedAt(runnable);

    if (startedAt) {
      return formatDateTime(startedAt, { hideYear: true });
    }

    if (this.isAborted(runnable)) {
      return 'Not started';
    }

    return 'Not started yet';
  }

  getFinishedAt(runnable: TRunnable): string {
    return runnable.finishedAt ? formatDateTime(runnable.finishedAt, { hideYear: true }) : 'Not finished yet';
  }

  protected abstract isAborted(_runnable: TRunnable): boolean;

  protected abstract isRunnableStarted(runnable: TRunnable): boolean;

  protected abstract getDurationStartedAt(runnable: TRunnable): Date | undefined;

  protected getDurationFinishedAt(runnable: TRunnable): Date | undefined {
    return runnable.finishedAt ? new Date(runnable.finishedAt) : undefined;
  }

  getDuration(runnable: TRunnable): string | { start?: Date; end?: Date } {
    if (this.isRunnableStarted(runnable)) {
      const start = this.getDurationStartedAt(runnable);
      const end = this.getDurationFinishedAt(runnable);

      if (start) {
        return { start, end };
      }
    }

    return this.isAborted(runnable) ? 'Not started' : 'Not started yet';
  }

  getAbortReason(runnable: TRunnable): string | undefined {
    return this.isAborted(runnable) ? runnable.abortReason || 'No reason given' : undefined;
  }

  getCreditCost(runnable: TRunnable, withoutText?: boolean): string | undefined {
    if (withoutText) {
      return runnable.creditCost ? `${runnable.creditCost}` : undefined;
    }

    return runnable.creditCost
      ? `${runnable.creditCost} ${pluralizeWordWithCount('credit', runnable.creditCost || 1)}`
      : undefined;
  }

  protected abstract getCommitMessage(runnable: TRunnable): string | undefined;

  protected abstract getPullRequestId(runnable: TRunnable): number | undefined;

  protected abstract getTag(runnable: TRunnable): string | undefined;

  getTriggerType(runnable: TRunnable): string | undefined {
    if (this.getPullRequestId(runnable)) {
      return TriggerTypes.PULL_REQUEST;
    }
    if (this.getTag(runnable)) {
      return TriggerTypes.TAG;
    }
    return TriggerTypes.PUSH;
  }

  hasTriggerData(runnable: TRunnable): boolean {
    return Boolean(this.getTriggerDescription(runnable));
  }

  getTriggerData(runnable: TRunnable): string {
    const description = this.getTriggerDescription(runnable);
    const emptyDescription =
      this.getTriggerType(runnable) === TriggerTypes.PULL_REQUEST ? 'No description' : 'No commit message';
    return description || emptyDescription;
  }

  private getTriggerDescription(runnable: TRunnable): string | undefined {
    return this.getCommitMessage(runnable)?.split('\n').slice(1).join('\n').trim() || undefined;
  }

  abstract getParameters(runnable: TRunnable): string;
}
